import {
  Candidate,
  Company,
  CompanyDocument,
  CompanyQuery,
  CompanyQueryVariables,
  Job,
  JobsLocationsDocument,
  JobsLocationsQuery,
  JobsLocationsQueryVariables,
  JobsSearchOrderBy,
  LocaleEnum,
  MeDocument,
  MeQuery,
  MeQueryVariables,
  Metadata,
  MetadataDocument,
  MetadataQuery,
  MetadataQueryVariables,
  PageEnum,
  Readable,
  SearchJobsDocument,
  SearchJobsQuery,
  SearchJobsQueryVariables,
  SearchReadablesDocument,
  SearchReadablesQuery,
  SearchReadablesQueryVariables,
} from 'generated/apolloComponents'
import I18nProvider from 'next-translate/I18nProvider'
import useTranslation from 'next-translate/useTranslation'
import React from 'react'
import { addApolloState, initializeApollo } from '@/api/apollo'

import CustomHead from '@/components/molecules/CustomHead'
import CompanyTemplate from '@/components/templates/Company/Company'
import companyEn from '../../../locales/en/company.json'
import companyPl from '../../../locales/pl/company.json'
import commonEn from '../../../locales/en/common.json'
import commonPl from '../../../locales/pl/common.json'
import benefitPl from '../../../locales/pl/benefit.json'
import benefitEn from '../../../locales/en/benefit.json'
import toolsEn from '../../../locales/en/tools.json'
import toolsPl from '../../../locales/pl/tools.json'
import hwmEn from '../../../locales/en/hwm.json'
import hwmPl from '../../../locales/pl/hwm.json'
import codeEn from '../../../locales/en/code.json'
import codePl from '../../../locales/pl/code.json'
import testEn from '../../../locales/en/test.json'
import testPl from '../../../locales/pl/test.json'
import { ScrollRestorer } from '@/components/atoms/ScrollRestorer'
import { countryFromHeader } from '@/helpers/url'

interface IProps {
  companyData: Company
  articles: Readable[]
  jobsLocations: string[]
  jobs: Job[]
  totalJobsCount: number
  metaData: Metadata
}

const CompanyProfile = ({
  companyData,
  articles,
  jobsLocations,
  jobs,
  totalJobsCount,
  metaData,
}: IProps) => {
  const { t, lang } = useTranslation('common')
  const company =
    companyData.locale === 'en' || lang === 'en' ? companyEn : companyPl
  const common =
    companyData.locale === 'en' || lang === 'en' ? commonEn : commonPl
  const benefit =
    companyData.locale === 'en' || lang === 'en' ? benefitEn : benefitPl
  const tools = companyData.locale === 'en' || lang === 'en' ? toolsEn : toolsPl
  const hwm = companyData.locale === 'en' || lang === 'en' ? hwmEn : hwmPl
  const code = companyData.locale === 'en' || lang === 'en' ? codeEn : codePl
  const test = companyData.locale === 'en' || lang === 'en' ? testEn : testPl

  return (
    <>
      <CustomHead metaData={metaData} />
      <I18nProvider
        lang={companyData.locale ? companyData.locale : lang}
        namespaces={{ company, common, benefit, tools, hwm, code, test }}
      >
        <ScrollRestorer />
        <CompanyTemplate
          company={companyData}
          articles={articles}
          jobsLocations={jobsLocations}
          jobs={jobs}
          domainLang={lang}
          totalJobsCount={totalJobsCount}
        />
      </I18nProvider>
    </>
  )
}

export const getServerSideProps = async (context: any) => {
  const page: number = 1
  const perPage: number = 20
  const apolloClient = initializeApollo({ ctx: context })

  const companyData = await apolloClient.query<
    CompanyQuery,
    CompanyQueryVariables
  >({
    query: CompanyDocument,
    variables: {
      id: context.params?.slug,
      language: LocaleEnum[context.locale as 'pl' | 'en'],
      viewToken: context.query?.viewToken,
    },
    errorPolicy: 'all',
  })

  if (companyData.errors) {
    return {
      notFound: true,
    }
  }

  if (companyData.data.company?.id !== context.params?.slug) {
    return {
      redirect: {
        destination: `/companies/profiles/${companyData.data.company?.id}`,
        permanent: true,
      },
    }
  }

  if (companyData.data.company?.locale === 'pl' && context.locale === 'en') {
    return {
      redirect: {
        destination: `https://${process.env.PL_HOST}/companies/profiles/${companyData.data.company?.id}`,
        permanent: true,
      },
    }
  }
  // TODO: uncomment/remove if needed
  // if (companyData.data.company?.locale === 'en' && context.locale === 'pl') {
  //   return {
  //     redirect: {
  //       destination: `https://${process.env.EN_HOST}/companies/profiles/${companyData.data.company?.id}`,
  //       permanent: true,
  //     },
  //   }
  // }

  const readableData = apolloClient.query<
    SearchReadablesQuery,
    SearchReadablesQueryVariables
  >({
    query: SearchReadablesDocument,
    variables: {
      perPage: 100,
      language: LocaleEnum[context.locale as 'pl' | 'en'],
      filters: { company: context.params?.slug },
    },
  })

  const { data: me } = await apolloClient.query<MeQuery, MeQueryVariables>({
    query: MeDocument,
  })
  const person = me.me as Candidate

  const jobFilters = companyData.data.company
    ? {
        company: [companyData.data.company.id],
      }
    : {}
  const variables: SearchJobsQueryVariables = {
    page: page,
    perPage: perPage,
    filters: jobFilters,
    language: LocaleEnum[context.locale as 'pl' | 'en'],
    country: countryFromHeader(context),
  }
  if (person && person.rejectedJobs)
    variables.exclude = [...person.rejectedJobs]

  if (companyData.data.company?.sorting === 'brackets')
    variables.order = {
      field: 'SHOW_SALARY',
      direction: 'DESC',
    } as JobsSearchOrderBy

  const jobsData = apolloClient.query<
    SearchJobsQuery,
    SearchJobsQueryVariables
  >({
    query: SearchJobsDocument,
    variables: variables,
  })

  const jobsLocationsData = apolloClient.query<
    JobsLocationsQuery,
    JobsLocationsQueryVariables
  >({
    query: JobsLocationsDocument,
    variables: {
      language: LocaleEnum[context.locale as 'pl' | 'en'],
    },
  })

  const metaData = apolloClient.query<MetadataQuery, MetadataQueryVariables>({
    query: MetadataDocument,
    variables: {
      pageType: PageEnum.CompanyDetails,
      language: LocaleEnum[context.locale as 'pl' | 'en'],
      id: context.params?.slug,
    },
  })

  const [
    readableResponse,
    jobsResponse,
    jobsLocationsResponse,
    metaResponse,
  ]: any = await Promise.all([
    readableData,
    jobsData,
    jobsLocationsData,
    metaData,
  ])

  const concatJobs = [...jobsResponse.data.searchJobs.nodes!]
  const jobsUniqueByKey = [
    // @ts-ignore
    ...new Map(concatJobs.map((item) => [item.id, item])).values(),
  ]

  return addApolloState(apolloClient, {
    props: {
      metaData: metaResponse.data.metadata,
      companyData: companyData.data.company,
      articles: readableResponse.data.searchReadables.nodes,
      jobsLocations: jobsLocationsResponse.data.jobsLocations,
      totalJobsCount: jobsResponse.data.searchJobs.totalCount,
      jobs: jobsUniqueByKey,
    },
  })
}

export default CompanyProfile
